import { twJoin } from 'tailwind-merge';

/**
 * Global component that serves as a wrapper
 * for rendering the text/HTML users can insert into the WYSIWYG editor
 */
type UserTextProps = {
    htmlContent: string;
    'data-testid'?: string;
    isGrapesJs?: boolean;
};

/**
 * Sanitizes and fixes HTML content to ensure it is valid and balanced.
 * This handles issues when GrapeJS splits content in the middle of nested structures.
 * 1. Removes orphan closing tags (e.g. </div> when no div is open).
 * 2. Closes unclosed opening tags at the end.
 */
const sanitizeHtmlContent = (html: string): string => {
    if (!html) {
        return '';
    }

    // Basic list of void elements that don't need closing
    const voidElements = new Set([
        'area',
        'base',
        'br',
        'col',
        'embed',
        'hr',
        'img',
        'input',
        'link',
        'meta',
        'param',
        'source',
        'track',
        'wbr',
    ]);

    let balancedHtml = '';
    const stack: string[] = [];
    const tagRegex = /<\/?([a-zA-Z0-9-]+)[^>]*>/g;

    let lastIndex = 0;
    let match;

    while ((match = tagRegex.exec(html)) !== null) {
        // Append text content before the tag
        balancedHtml += html.substring(lastIndex, match.index);

        const fullTag = match[0];
        const tagName = match[1].toLowerCase();
        const isClosing = fullTag.startsWith('</');
        const isSelfClosing = fullTag.endsWith('/>');

        if (isClosing) {
            // It's a closing tag
            // Check if tagName is in stack using lastIndexOf
            const stackIndex = stack.lastIndexOf(tagName);
            if (stackIndex !== -1) {
                // It matches something deeper. Close everything above it.
                while (stack.length > stackIndex + 1) {
                    const top = stack.pop();
                    balancedHtml += `</${top}>`;
                }
                stack.pop(); // Pop the matching tag
                balancedHtml += fullTag;
            } else {
                // Not in stack at all - purely orphan. Remove.
            }
        } else {
            // Opening tag
            balancedHtml += fullTag;
            if (!isSelfClosing && !voidElements.has(tagName)) {
                stack.push(tagName);
            }
        }

        lastIndex = tagRegex.lastIndex;
    }

    // Append remaining text
    balancedHtml += html.substring(lastIndex);

    // Close any remaining open tags
    while (stack.length > 0) {
        const top = stack.pop();
        balancedHtml += `</${top}>`;
    }

    return balancedHtml;
};

export const UserText: FC<UserTextProps> = ({ isGrapesJs, htmlContent, ...props }) => {
    const attr = {
        'data-testid': props['data-testid'],
    };

    const sanitizedContent = sanitizeHtmlContent(htmlContent);

    // This prevents hydration mismatch when GrapeJS splits create empty sections
    if (!sanitizedContent) {
        return null;
    }

    return (
        <section
            className={twJoin('user-text', isGrapesJs && 'grapesjs-text')}
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            {...attr}
        />
    );
};

export default UserText;
